import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { trackControl } from "@/lib/analytics";
import { jobKeywords, type PostingKeyword } from "@/lib/api/catalogue";
import { sameText, toolFit, type ToolFit } from "@/lib/tools/check";
import { MIN_RESUME_CHARS, resumeTextOf } from "@/lib/tools/resumeText";
import { ONE_AT_A_TIME, useSingleFlightMutation } from "./singleFlight";

const SAME_TEXT = "Both boxes hold the same text. Put your resume in the second one.";
const SHORT_JOB = "Paste more of the job. The whole text is best.";
const SHORT_RESUME = "Paste more of your resume, or pick a file.";
/** The catalogue over its allowance, or down: both read the same. */
const TRY_AGAIN = "Try again in a moment.";

/** A read that takes longer than this says so. A typical one shows nothing. */
export const SLOW_READ_MS = 300;

/** A text's length without its white space, as the catalogue counts a job's text. */
function written(text: string): number {
    return text.replace(/\s/g, "").length;
}

/** Why these texts cannot be checked, or null. `job` is null when the job is a posting. */
function refusal(job: string | null, resume: string): string | null {
    if (job !== null && sameText(job, resume)) return SAME_TEXT;
    if (job !== null && written(job) < MIN_RESUME_CHARS) return SHORT_JOB;
    if (written(resume) < MIN_RESUME_CHARS) return SHORT_RESUME;
    return null;
}

export interface ResumeCheck {
    job: string;
    setJob: (text: string) => void;
    resume: string;
    setResume: (text: string) => void;
    /** Fills the resume box from a file read in this browser. */
    pickFile: (file: File) => Promise<void>;
    /** A picked file is taking long enough to say so. */
    readingFile: boolean;
    problem: string | null;
    check: () => void;
    checking: boolean;
    /** The keywords checked against, null until a check has found them. */
    keywords: PostingKeyword[] | null;
    /** The resume box against the keywords, following what is typed. */
    fit: ToolFit | null;
    /** Puts both boxes back, as they were before signing in. */
    restore: (job: string, resume: string) => void;
}

/**
 * The free resume check. The resume never leaves this browser: only the
 * job's text goes to the catalogue, once a check, and the count follows
 * the resume box with no further call. `given` is a posting's keywords:
 * the job is known, so the job box is not read and nothing is asked, and
 * the fit follows the box before any check.
 */
export function useResumeCheck(given: PostingKeyword[] | null = null): ResumeCheck {
    const { pathname } = useLocation();
    const [job, setJobText] = useState("");
    const [resume, setResumeText] = useState("");
    const [problem, setProblem] = useState<string | null>(null);
    const [readingFile, setReadingFile] = useState(false);
    const [keywords, setKeywords] = useState<PostingKeyword[] | null>(null);
    // Bumped by a change of the job, so an answer for the old text is dropped.
    const jobVersion = useRef(0);
    // Bumped by a pick and by typing, so a slow file writes over neither.
    const resumeVersion = useRef(0);
    // Ends a read once a new pick or typing replaces it, and takes its worker with it.
    const fileRead = useRef<AbortController | null>(null);
    const reading = useSingleFlightMutation(
        { mutationFn: (text: string) => jobKeywords("", text) },
        ONE_AT_A_TIME,
    );

    useEffect(() => () => fileRead.current?.abort(), []);

    const typedResume = useDeferredValue(resume);
    const measured = keywords ?? given;
    const fit = useMemo(
        () => (measured ? toolFit(measured, typedResume) : null),
        [measured, typedResume],
    );

    const setJob = (text: string) => {
        jobVersion.current += 1;
        setJobText(text);
        setKeywords(null);
        setProblem(null);
    };

    const setResume = (text: string) => {
        resumeVersion.current += 1;
        fileRead.current?.abort();
        setResumeText(text);
        setProblem(null);
        setReadingFile(false);
    };

    const settle = (found: PostingKeyword[]) => {
        setKeywords(found);
        const { asked, covered } = toolFit(found, resume);
        trackControl("tool_check_run", { source: "ResumeCheck", target: pathname, asked, covered });
    };

    const check = () => {
        const refused = refusal(given ? null : job, resume);
        setProblem(refused);
        if (refused) return;
        if (given) return settle(given);
        const version = jobVersion.current;
        reading.mutate(job, {
            onSuccess: (answer) => {
                if (version === jobVersion.current) settle(answer.keywords);
            },
            onError: () => {
                if (version === jobVersion.current) setProblem(TRY_AGAIN);
            },
        });
    };

    const pickFile = async (file: File) => {
        resumeVersion.current += 1;
        const version = resumeVersion.current;
        fileRead.current?.abort();
        const read = new AbortController();
        fileRead.current = read;
        setProblem(null);
        const slow = setTimeout(
            () => setReadingFile(version === resumeVersion.current),
            SLOW_READ_MS,
        );
        const answer = await resumeTextOf(file, read.signal);
        clearTimeout(slow);
        if (version !== resumeVersion.current) return;
        setReadingFile(false);
        if (answer.text === null) setProblem(answer.problem);
        else setResumeText(answer.text);
    };

    const restore = (savedJob: string, savedResume: string) => {
        setJob(savedJob);
        setResume(savedResume);
    };

    return {
        job,
        setJob,
        resume,
        setResume,
        pickFile,
        readingFile,
        problem,
        check,
        checking: reading.isPending,
        keywords,
        fit,
        restore,
    };
}
